import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
  Tabs,
} from '@/components/ui'
import { fetchColaboradores } from '@/features/rh/api'
import {
  anexarDocumentoRh,
  aprovarSolicitacaoDocumento,
  assinarDocumento,
  criarSolicitacaoDocumento,
  fetchGedDocumentos,
  fetchMeusDocumentos,
  fetchSolicitacoesDocumento,
  fetchTiposDocumento,
  recusarSolicitacaoDocumento,
} from './api'

type Aba = 'docs' | 'solicitacoes' | 'catalogo'

function tone(status?: string) {
  const s = (status || '').toLowerCase()
  if (/(pend|aguard|analise)/.test(s)) return 'pendente' as const
  if (/(assin|aprov|ok|conclu|dispon)/.test(s)) return 'ativo' as const
  if (/(recus|rejeit)/.test(s)) return 'inativo' as const
  return 'pendente' as const
}

function fluxoLabel(fluxo?: string) {
  if (fluxo === 'admissional') return 'Admissional'
  if (fluxo === 'sob_demanda') return 'Sob demanda'
  if (fluxo === 'solicitacao') return 'Solicitação'
  return 'Padrão'
}

function fmtDate(iso?: string | null) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('pt-BR')
  } catch {
    return '—'
  }
}

export function DocumentosPage() {
  const { user, isRH, canEdit, perfil } = useAuth()
  const qc = useQueryClient()
  const colabId = user?.colaborador_id ? String(user.colaborador_id) : ''
  const isGedUser =
    isRH || canEdit('documentos') || ['admin', 'rh', 'analista'].includes(perfil)
  const isGestorPerfil = perfil === 'gestor'
  const podeDecidir = isGedUser || isGestorPerfil

  const [aba, setAba] = useState<Aba>('docs')
  const [modo, setModo] = useState<'ged' | 'meus'>(() => (isGedUser ? 'ged' : 'meus'))
  const [busca, setBusca] = useState('')
  const [q, setQ] = useState('')
  /** Filtro explícito de colaborador (GED/meus). Não usa o colab do usuário logado no GED. */
  const [alvoId, setAlvoId] = useState('')
  const [alvoNome, setAlvoNome] = useState('')
  const [pickerQ, setPickerQ] = useState('')
  const [filtroSolic, setFiltroSolic] = useState<
    'pendente' | 'pendente_gestor' | 'pendente_rh' | 'todas' | 'aprovado' | 'recusado'
  >(isGedUser ? 'pendente_rh' : isGestorPerfil ? 'pendente_gestor' : 'todas')
  const [tipoSolic, setTipoSolic] = useState('')
  const [motivoSolic, setMotivoSolic] = useState('')
  const [finalidadeSolic, setFinalidadeSolic] = useState('')
  const [alvoSolic, setAlvoSolic] = useState('')
  const [anexoTipo, setAnexoTipo] = useState('')
  const [anexoTitulo, setAnexoTitulo] = useState('')
  const [anexoFile, setAnexoFile] = useState<File | null>(null)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState<'success' | 'danger' | 'info'>('info')

  const ged = useQuery({
    queryKey: ['documentos', 'ged', q, alvoId],
    queryFn: () =>
      fetchGedDocumentos({
        busca: q || undefined,
        // Só filtra colaborador se o RH escolheu alguém — nunca o próprio vínculo no "GED todos"
        colaboradorId: alvoId || undefined,
        limit: 80,
      }),
    enabled: aba === 'docs' && modo === 'ged' && isGedUser,
  })

  const meus = useQuery({
    queryKey: ['documentos', 'meus', isGedUser ? alvoId : colabId],
    queryFn: () =>
      fetchMeusDocumentos({
        colaboradorId: isGedUser ? alvoId || undefined : undefined,
      }),
    enabled:
      aba === 'docs' &&
      modo === 'meus' &&
      (isGedUser ? Boolean(alvoId) : Boolean(colabId)),
  })

  const picker = useQuery({
    queryKey: ['documentos', 'picker', pickerQ],
    queryFn: () =>
      fetchColaboradores({
        busca: pickerQ || undefined,
        status: 'ativo',
        page: 1,
        por_pagina: 12,
      }),
    enabled: isGedUser && pickerQ.length >= 2,
  })

  const solicitacoes = useQuery({
    queryKey: ['documentos', 'solicitacoes', filtroSolic, alvoId],
    queryFn: () =>
      fetchSolicitacoesDocumento({
        status: filtroSolic === 'todas' ? undefined : filtroSolic,
        colaboradorId: isGedUser && alvoId ? alvoId : undefined,
        limit: 120,
      }),
    enabled: aba === 'solicitacoes',
  })

  const tipos = useQuery({
    queryKey: ['documentos', 'tipos'],
    queryFn: () => fetchTiposDocumento(),
    enabled: aba === 'catalogo' || aba === 'solicitacoes' || (aba === 'docs' && isGedUser),
  })

  const tiposAnexaveis = useMemo(
    () =>
      (tipos.data || []).filter((t) => {
        if (t.fluxo === 'sob_demanda' || t.fluxo === 'solicitacao') return false
        return t.categoria === 'rh_padrao' || t.categoria === 'rh_gerado' || t.fluxo === 'admissional'
      }),
    [tipos.data],
  )

  const tiposSolicitaveis = useMemo(
    () =>
      (tipos.data || []).filter((t) =>
        isGedUser || isGestorPerfil
          ? t.fluxo === 'solicitacao' || t.fluxo === 'sob_demanda'
          : t.fluxo === 'solicitacao',
      ),
    [tipos.data, isGedUser, isGestorPerfil],
  )

  const docsQuery = modo === 'ged' ? ged : meus
  const lista = docsQuery.data?.dados || []
  const precisaEscolher = isGedUser && modo === 'meus' && !alvoId
  const listaSolic = solicitacoes.data?.dados || []

  function flash(text: string, tone: 'success' | 'danger' | 'info' = 'success') {
    setMsgTone(tone)
    setMsg(text)
  }

  const anexarMut = useMutation({
    mutationFn: () => {
      if (!alvoId) throw new Error('Selecione o colaborador.')
      if (!anexoTipo) throw new Error('Selecione o tipo de documento.')
      if (!anexoFile) throw new Error('Selecione o arquivo.')
      return anexarDocumentoRh({
        colaborador_id: alvoId,
        tipo_codigo: anexoTipo,
        arquivo: anexoFile,
        titulo: anexoTitulo || undefined,
      })
    },
    onSuccess: async () => {
      flash('Documento anexado e disponibilizado ao colaborador.')
      setAnexoFile(null)
      setAnexoTitulo('')
      await qc.invalidateQueries({ queryKey: ['documentos'] })
    },
    onError: (err) => {
      flash(err instanceof ApiError ? err.message : 'Falha ao anexar documento.', 'danger')
    },
  })

  const criarMut = useMutation({
    mutationFn: () => {
      if (!tipoSolic) throw new Error('Selecione o tipo.')
      const tipo = tiposSolicitaveis.find((t) => t.codigo === tipoSolic || t.id === tipoSolic)
      return criarSolicitacaoDocumento({
        tipo_codigo: tipo?.codigo || tipoSolic,
        colaborador_id:
          tipo?.fluxo === 'sob_demanda'
            ? alvoSolic || undefined
            : isGedUser && alvoSolic
              ? alvoSolic
              : undefined,
        motivo: motivoSolic || finalidadeSolic,
        finalidade: finalidadeSolic || motivoSolic,
      })
    },
    onSuccess: async () => {
      flash('Solicitação enviada. Gestor e RH serão notificados conforme o fluxo.')
      setMotivoSolic('')
      setFinalidadeSolic('')
      setTipoSolic('')
      setAlvoSolic('')
      await qc.invalidateQueries({ queryKey: ['documentos', 'solicitacoes'] })
    },
    onError: (err) => {
      flash(err instanceof ApiError ? err.message : 'Falha ao solicitar.', 'danger')
    },
  })

  const aprovarMut = useMutation({
    mutationFn: (payload: { id: string; arquivo?: File | null; observacao?: string }) =>
      aprovarSolicitacaoDocumento(payload.id, {
        observacao: payload.observacao,
        arquivo: payload.arquivo,
      }),
    onSuccess: async () => {
      flash('Etapa confirmada / solicitação atualizada.')
      await qc.invalidateQueries({ queryKey: ['documentos'] })
    },
    onError: (err) => flash(err instanceof ApiError ? err.message : 'Falha ao aprovar.', 'danger'),
  })

  const recusarMut = useMutation({
    mutationFn: ({ id, justificativa }: { id: string; justificativa: string }) =>
      recusarSolicitacaoDocumento(id, justificativa),
    onSuccess: async () => {
      flash('Solicitação recusada. Colaborador notificado.', 'info')
      await qc.invalidateQueries({ queryKey: ['documentos', 'solicitacoes'] })
    },
    onError: (err) => flash(err instanceof ApiError ? err.message : 'Falha ao recusar.', 'danger'),
  })

  const assinarMut = useMutation({
    mutationFn: (id: string) => assinarDocumento(id),
    onSuccess: async () => {
      flash('Documento assinado.')
      await qc.invalidateQueries({ queryKey: ['documentos'] })
    },
    onError: (err) => flash(err instanceof ApiError ? err.message : 'Falha ao assinar.', 'danger'),
  })

  const tabs: { id: Aba; label: string }[] = [
    { id: 'docs', label: 'Documentos' },
    {
      id: 'solicitacoes',
      label: isGedUser
        ? `Solicitações${solicitacoes.data?.pendentes ? ` (${solicitacoes.data.pendentes})` : ''}`
        : 'Minhas solicitações',
    },
    { id: 'catalogo', label: 'Catálogo / fluxos' },
  ]

  return (
    <section className="hi-page">
      <PageHeader
        title="Documentos"
        subtitle="GED: anexar admissionais, listar, solicitações (gestor → RH) e assinatura."
        actions={
          <>
            <Link className="btn btn-secondary" to="/app/dp/assinatura">
              Assinatura
            </Link>
            <a className="btn btn-secondary" href={legacyAppUrl('#/documentos')}>
              GED legado
            </a>
          </>
        }
      />

      {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}

      <Tabs items={tabs} value={aba} onChange={(id) => setAba(id as Aba)} label="Abas documentos" />

      {aba === 'docs' ? (
        <>
          {isGedUser ? (
            <form
              className="hi-filters"
              onSubmit={(e) => {
                e.preventDefault()
                setQ(busca.trim())
              }}
            >
              <select
                value={modo}
                onChange={(e) => {
                  const m = e.target.value as 'ged' | 'meus'
                  setModo(m)
                  if (m === 'ged') {
                    setAlvoId('')
                    setAlvoNome('')
                  }
                }}
                aria-label="Modo"
              >
                <option value="ged">GED (todos)</option>
                <option value="meus">Por colaborador</option>
              </select>
              {modo === 'ged' ? (
                <input
                  type="search"
                  placeholder="Buscar título, colaborador, tipo…"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              ) : (
                <input
                  type="search"
                  placeholder="Buscar colaborador (nome)…"
                  value={pickerQ}
                  onChange={(e) => setPickerQ(e.target.value)}
                />
              )}
              {modo === 'ged' ? (
                <button type="submit" className="btn btn-primary">
                  Buscar
                </button>
              ) : null}
              {alvoId ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setAlvoId('')
                    setAlvoNome('')
                    setPickerQ('')
                  }}
                >
                  Limpar colaborador{alvoNome ? ` (${alvoNome})` : ''}
                </button>
              ) : null}
            </form>
          ) : null}

          {isGedUser && pickerQ.length >= 2 ? (
            <Section title="Selecionar colaborador" flush>
              <ul className="hi-list">
                {(picker.data?.dados || []).map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="card card-interactive hi-row"
                      style={{ width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer' }}
                      onClick={() => {
                        setAlvoId(c.id)
                        setAlvoNome(c.nome)
                        setPickerQ('')
                        setModo('meus')
                      }}
                    >
                      <div className="hi-row-main">
                        <strong>{c.nome}</strong>
                        <span>{c.cargo_nome || 'Sem cargo'}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          {isGedUser ? (
            <Section title="Anexar documento ao colaborador">
              <Alert tone="info">
                Admissionais (Código de Ética, Termo de ferramentas, etc.) precisam ser anexados
                aqui para aparecerem na lista e para assinatura do colaborador.
              </Alert>
              {!alvoId ? (
                <Muted>
                  Busque e selecione um colaborador acima (modo “Por colaborador”) para anexar.
                </Muted>
              ) : (
                <div className="hi-fields">
                  <Muted>
                    Destino: <strong>{alvoNome || alvoId}</strong>
                  </Muted>
                  <label className="hi-field">
                    <span>Tipo</span>
                    <select
                      value={anexoTipo}
                      onChange={(e) => setAnexoTipo(e.target.value)}
                      disabled={anexarMut.isPending}
                    >
                      <option value="">Selecione…</option>
                      {tiposAnexaveis.map((t) => (
                        <option key={t.id} value={t.codigo}>
                          {t.nome}
                          {t.exige_assinatura ? ' (assinatura)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="hi-field">
                    <span>Título (opcional)</span>
                    <input
                      value={anexoTitulo}
                      onChange={(e) => setAnexoTitulo(e.target.value)}
                      placeholder="Deixe vazio para usar o nome do tipo"
                    />
                  </label>
                  <label className="hi-field">
                    <span>Arquivo</span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                      onChange={(e) => setAnexoFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={anexarMut.isPending || !anexoTipo || !anexoFile}
                    onClick={() => anexarMut.mutate()}
                  >
                    {anexarMut.isPending ? 'Enviando…' : 'Anexar e disponibilizar'}
                  </button>
                </div>
              )}
            </Section>
          ) : null}

          {!isGedUser && !colabId ? (
            <Alert tone="info">
              Conta sem colaborador vinculado. Peça ao RH para associar{' '}
              <strong>{user?.email || 'este e-mail'}</strong>.
            </Alert>
          ) : null}

          <KpiGrid>
            <Kpi label="Documentos" value={String(lista.length)} />
            <Kpi label="Pendentes" value={String(docsQuery.data?.pendentes ?? 0)} />
          </KpiGrid>

          <Section
            title={modo === 'ged' ? 'GED recente' : 'Documentos para assinatura / consulta'}
            flush
          >
            {docsQuery.isLoading ? <Muted>Carregando…</Muted> : null}
            {docsQuery.isError ? (
              <Alert tone="danger">
                {docsQuery.error instanceof ApiError
                  ? docsQuery.error.message
                  : 'Falha ao listar documentos.'}
              </Alert>
            ) : null}
            {!docsQuery.isLoading && lista.length === 0 && !precisaEscolher ? (
              <Empty>
                Nenhum documento. Admissionais precisam ser anexados pelo RH na admissão.
              </Empty>
            ) : null}
            {lista.length > 0 ? (
              <DataTable>
                <thead>
                  <tr>
                    {modo === 'ged' ? <th>Colaborador</th> : null}
                    <th>Título</th>
                    <th>Tipo / fluxo</th>
                    <th>Status</th>
                    <th>Criado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {lista.map((d) => {
                    const podeAssinar =
                      !isGedUser &&
                      /pendente_assinatura|disponivel/i.test(String(d.status || '')) &&
                      (d.exige_assinatura || /pendente_assinatura/i.test(String(d.status || '')))
                    return (
                      <tr key={d.id}>
                        {modo === 'ged' ? <td>{d.colaborador_nome || '—'}</td> : null}
                        <td>{String(d.titulo || d.titulo_exibicao || '—')}</td>
                        <td>
                          {String(
                            d.tipo_nome ||
                              (typeof d.tipo === 'object' && d.tipo && 'nome' in d.tipo
                                ? d.tipo.nome
                                : d.tipo) ||
                              '—',
                          )}
                          <div>
                            <Muted>{fluxoLabel(String(d.tipo_fluxo || ''))}</Muted>
                          </div>
                        </td>
                        <td>
                          <StatusBadge tone={tone(String(d.status))}>
                            {String(d.status_display || d.status || '—')}
                          </StatusBadge>
                        </td>
                        <td>{fmtDate(d.created_at)}</td>
                        <td>
                          {podeAssinar ? (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={assinarMut.isPending}
                              onClick={() => assinarMut.mutate(d.id)}
                            >
                              Assinar
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </DataTable>
            ) : null}
          </Section>
        </>
      ) : null}

      {aba === 'solicitacoes' ? (
        <div className="hi-stack">
          <Section title="Nova solicitação">
            <Alert tone="info">
              Fluxo: <strong>1)</strong> colaborador escolhe o documento e informa a finalidade →{' '}
              <strong>2)</strong> gestor confirma → <strong>3)</strong> RH emite/disponibiliza →{' '}
              <strong>4)</strong> download na plataforma + notificação de conclusão. Desligamento
              indenizado continua sob demanda (gestor/RH).
            </Alert>
            <div className="hi-fields">
              <label className="hi-field">
                <span>Documento / solicitação</span>
                <select
                  value={tipoSolic}
                  onChange={(e) => setTipoSolic(e.target.value)}
                  disabled={criarMut.isPending}
                >
                  <option value="">Selecione…</option>
                  {tiposSolicitaveis.map((t) => (
                    <option key={t.id} value={t.codigo}>
                      {t.nome}
                    </option>
                  ))}
                </select>
              </label>
              {(isGedUser || isGestorPerfil) &&
              tiposSolicitaveis.find((t) => t.codigo === tipoSolic)?.fluxo === 'sob_demanda' ? (
                <label className="hi-field">
                  <span>Colaborador (id)</span>
                  <input
                    value={alvoSolic}
                    onChange={(e) => setAlvoSolic(e.target.value)}
                    placeholder="colaborador_id"
                  />
                </label>
              ) : null}
              <label className="hi-field">
                <span>Finalidade</span>
                <input
                  value={finalidadeSolic}
                  onChange={(e) => setFinalidadeSolic(e.target.value)}
                  placeholder="Ex.: financiamento imobiliário, declaração escolar…"
                />
              </label>
              <label className="hi-field">
                <span>Dados / detalhe</span>
                <textarea
                  rows={3}
                  value={motivoSolic}
                  onChange={(e) => setMotivoSolic(e.target.value)}
                  placeholder="Preencha os dados necessários da solicitação…"
                />
              </label>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: '0.75rem' }}
              disabled={criarMut.isPending || !tipoSolic}
              onClick={() => criarMut.mutate()}
            >
              {criarMut.isPending ? 'Enviando…' : 'Enviar solicitação'}
            </button>
          </Section>

          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <select
              value={filtroSolic}
              onChange={(e) => setFiltroSolic(e.target.value as typeof filtroSolic)}
              aria-label="Status"
            >
              <option value="todas">Todas</option>
              <option value="pendente">Em andamento</option>
              <option value="pendente_gestor">Aguardando gestor</option>
              <option value="pendente_rh">Aguardando RH</option>
              <option value="aprovado">Concluídas</option>
              <option value="recusado">Recusadas</option>
            </select>
          </form>

          <KpiGrid>
            <Kpi label="Na lista" value={String(listaSolic.length)} />
            <Kpi label="Em andamento" value={String(solicitacoes.data?.pendentes ?? 0)} />
          </KpiGrid>

          <Section
            title={
              isGedUser
                ? 'Painel RH / histórico'
                : isGestorPerfil
                  ? 'Fila do gestor / histórico'
                  : 'Minhas solicitações'
            }
            flush
          >
            {solicitacoes.isLoading ? <Muted>Carregando…</Muted> : null}
            {solicitacoes.isError ? (
              <Alert tone="danger">
                {solicitacoes.error instanceof ApiError
                  ? solicitacoes.error.message
                  : 'Falha ao listar solicitações.'}
              </Alert>
            ) : null}
            {!solicitacoes.isLoading && listaSolic.length === 0 ? (
              <Empty>Nenhuma solicitação neste filtro.</Empty>
            ) : null}
            {listaSolic.length > 0 ? (
              <DataTable>
                <thead>
                  <tr>
                    <th>Colaborador</th>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th>Solicitado</th>
                    <th>Confirmações</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {listaSolic.map((s) => {
                    const st = String(s.status || '')
                    const aguardaGestor = st === 'pendente_gestor' || st === 'pendente'
                    const aguardaRh = st === 'pendente_rh'
                    const podeGestor = (isGestorPerfil || isGedUser) && aguardaGestor
                    const podeRh = isGedUser && aguardaRh
                    return (
                      <tr key={s.id}>
                        <td>
                          <strong>{s.colaborador_nome || '—'}</strong>
                          {s.finalidade || s.motivo ? (
                            <div>
                              <Muted>{s.finalidade || s.motivo}</Muted>
                            </div>
                          ) : null}
                        </td>
                        <td>{s.tipo_nome || '—'}</td>
                        <td>
                          <StatusBadge tone={tone(s.status)}>
                            {s.status_display || s.status || '—'}
                          </StatusBadge>
                          {s.justificativa_recusa ? (
                            <div>
                              <Muted>Justificativa: {s.justificativa_recusa}</Muted>
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {fmtDate(s.created_at)}
                          {s.solicitante_nome ? (
                            <div>
                              <Muted>por {s.solicitante_nome}</Muted>
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <Muted>
                            Gestor:{' '}
                            {s.aprovado_gestor_em
                              ? `${fmtDate(s.aprovado_gestor_em)}${
                                  s.aprovado_gestor_por_nome
                                    ? ` (${s.aprovado_gestor_por_nome})`
                                    : ''
                                }`
                              : '—'}
                          </Muted>
                          <div>
                            <Muted>
                              RH:{' '}
                              {s.decidido_em
                                ? `${fmtDate(s.decidido_em)}${
                                    s.decidido_por_nome ? ` (${s.decidido_por_nome})` : ''
                                  }`
                                : '—'}
                            </Muted>
                          </div>
                        </td>
                        <td>
                          {podeDecidir && (podeGestor || podeRh) ? (
                            <div className="hi-filters" style={{ margin: 0, gap: 6 }}>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={aprovarMut.isPending || recusarMut.isPending}
                                onClick={() => {
                                  if (podeRh) {
                                    const input = document.createElement('input')
                                    input.type = 'file'
                                    input.accept = '.pdf,.docx,.doc,.png,.jpg,.jpeg'
                                    input.onchange = () => {
                                      const file = input.files?.[0] || null
                                      aprovarMut.mutate({ id: s.id, arquivo: file })
                                    }
                                    // RH pode concluir sem arquivo (depois anexa no GED)
                                    if (
                                      window.confirm(
                                        'Anexar o documento agora? OK = escolher arquivo; Cancelar = só marcar concluído.',
                                      )
                                    ) {
                                      input.click()
                                    } else {
                                      aprovarMut.mutate({ id: s.id })
                                    }
                                    return
                                  }
                                  aprovarMut.mutate({ id: s.id })
                                }}
                              >
                                {podeRh ? 'Emitir / concluir' : 'Confirmar'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                disabled={aprovarMut.isPending || recusarMut.isPending}
                                onClick={() => {
                                  const just = window.prompt(
                                    'Justificativa da recusa (obrigatória):',
                                  )
                                  if (just == null) return
                                  if (!just.trim()) {
                                    flash('Justificativa obrigatória.', 'danger')
                                    return
                                  }
                                  recusarMut.mutate({ id: s.id, justificativa: just.trim() })
                                }}
                              >
                                Recusar
                              </button>
                            </div>
                          ) : s.documento ? (
                            <Muted>Doc. liberado</Muted>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </DataTable>
            ) : null}
          </Section>
        </div>
      ) : null}

      {aba === 'catalogo' ? (
        <Section title="Tipos e regras de disponibilidade" flush>
          {tipos.isLoading ? <Muted>Carregando…</Muted> : null}
          {(tipos.data || []).length === 0 && !tipos.isLoading ? (
            <Empty>Catálogo vazio.</Empty>
          ) : null}
          {(tipos.data || []).length > 0 ? (
            <DataTable>
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Fluxo</th>
                  <th>Assinatura</th>
                  <th>Regra</th>
                </tr>
              </thead>
              <tbody>
                {(tipos.data || []).map((t) => (
                  <tr key={t.id}>
                    <td>
                      <strong>{t.nome}</strong>
                      <div>
                        <Muted>{t.codigo}</Muted>
                      </div>
                    </td>
                    <td>{t.fluxo_display || fluxoLabel(t.fluxo)}</td>
                    <td>{t.exige_assinatura ? 'Sim' : 'Não'}</td>
                    <td>
                      <Muted>
                        {t.descricao ||
                          (t.fluxo === 'admissional'
                            ? 'Anexar na admissão — aparece para o colaborador assinar.'
                            : t.fluxo === 'sob_demanda'
                              ? 'Só após solicitação do gestor e aprovação do RH.'
                              : t.fluxo === 'solicitacao'
                                ? 'Colaborador → gestor confirma → RH emite (download).'
                                : 'Fluxo padrão do GED.')}
                      </Muted>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : null}
        </Section>
      ) : null}
    </section>
  )
}
